import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Add request interceptor for debugging
axios.interceptors.request.use(request => {
  if (request.url.includes('/service-calls') && request.method === 'post') {
    console.log('=== AXIOS REQUEST ===');
    console.log('URL:', request.url);
    console.log('Data:', JSON.stringify(request.data, null, 2));
  }
  return request;
});

// Add response interceptor for debugging
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 422) {
      console.error('=== 422 ERROR DETAILS ===');
      console.error('Response:', error.response.data);
    }
    return Promise.reject(error);
  }
);

// ==================== AUTH ====================
const authHeader = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const authLogoutAll = async () => {
  try {
    const token = localStorage.getItem('authToken');
    await axios.post(`${API}/auth/logout-all`, {}, {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  } catch (error) {
    console.error('Logout-all request failed:', error);
  }
};

export const authMe = async () => {
  const response = await axios.get(`${API}/auth/me`, {
    withCredentials: true,
    headers: authHeader()
  });
  return response.data;
};

export const authLogout = async () => {
  try {
    await axios.post(`${API}/auth/logout`, {}, {
      withCredentials: true,
      headers: authHeader()
    });
  } catch (error) {
    // Non-fatal: still clear client state even if the server call fails
    console.error('Logout request failed:', error);
  }
};

// ==================== SERVICE CALLS ====================
export const getServiceCalls = async () => {
  try {
    const response = await axios.get(`${API}/service-calls`);
    return response.data;
  } catch (error) {
    console.error('Error fetching service calls:', error);
    throw error;
  }
};

export const getNextServiceCallNumber = async () => {
  try {
    const response = await axios.get(`${API}/service-calls/next-number`);
    return response.data.nextNumber;
  } catch (error) {
    console.error('Error fetching next service call number:', error);
    return null;
  }
};

export const addServiceCall = async (serviceCall) => {
  try {
    const response = await axios.post(`${API}/service-calls`, serviceCall);
    return response.data;
  } catch (error) {
    console.error('Error adding service call:', error);
    throw error;
  }
};

export const deleteServiceCall = async (id) => {
  try {
    await axios.delete(`${API}/service-calls/${id}`);
  } catch (error) {
    console.error('Error deleting service call:', error);
    throw error;
  }
};

export const updateServiceCall = async (id, serviceCall) => {
  try {
    const response = await axios.put(`${API}/service-calls/${id}`, serviceCall);
    return response.data;
  } catch (error) {
    console.error('Error updating service call:', error);
    throw error;
  }
};

// ==================== DAILY LOGS ====================
export const getDailyLogs = async () => {
  try {
    const response = await axios.get(`${API}/daily-logs`);
    return response.data;
  } catch (error) {
    console.error('Error fetching daily logs:', error);
    throw error;
  }
};

export const addDailyLog = async (log) => {
  try {
    const response = await axios.post(`${API}/daily-logs`, log);
    return response.data;
  } catch (error) {
    console.error('Error adding daily log:', error);
    throw error;
  }
};

export const deleteDailyLog = async (id) => {
  try {
    await axios.delete(`${API}/daily-logs/${id}`);
  } catch (error) {
    console.error('Error deleting daily log:', error);
    throw error;
  }
};

export const updateDailyLog = async (id, log) => {
  try {
    const response = await axios.put(`${API}/daily-logs/${id}`, log);
    return response.data;
  } catch (error) {
    console.error('Error updating daily log:', error);
    throw error;
  }
};

// ==================== DEAL CLOSURES ====================
export const getDealClosures = async () => {
  try {
    const response = await axios.get(`${API}/deal-closures`);
    return response.data;
  } catch (error) {
    console.error('Error fetching deal closures:', error);
    throw error;
  }
};

export const addDealClosure = async (deal) => {
  try {
    const response = await axios.post(`${API}/deal-closures`, deal);
    return response.data;
  } catch (error) {
    console.error('Error adding deal closure:', error);
    throw error;
  }
};

export const deleteDealClosure = async (id) => {
  try {
    await axios.delete(`${API}/deal-closures/${id}`);
  } catch (error) {
    console.error('Error deleting deal closure:', error);
    throw error;
  }
};

export const updateDealClosure = async (id, deal) => {
  try {
    const response = await axios.put(`${API}/deal-closures/${id}`, deal);
    return response.data;
  } catch (error) {
    console.error('Error updating deal closure:', error);
    throw error;
  }
};

// ==================== PROJECTS ====================
export const getProjects = async () => {
  try {
    // Add timestamp to prevent caching + no-cache headers
    const timestamp = new Date().getTime();
    const response = await axios.get(`${API}/projects?_t=${timestamp}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }
};

export const getProject = async (id) => {
  try {
    const response = await axios.get(`${API}/projects/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching project:', error);
    throw error;
  }
};

export const addProject = async (project) => {
  try {
    const response = await axios.post(`${API}/projects`, project);
    return response.data;
  } catch (error) {
    console.error('Error adding project:', error);
    throw error;
  }
};

export const deleteProject = async (id) => {
  try {
    await axios.delete(`${API}/projects/${id}`);
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};

export const updateProject = async (id, project) => {
  try {
    const response = await axios.put(`${API}/projects/${id}`, project);
    return response.data;
  } catch (error) {
    console.error('Error updating project:', error);
    throw error;
  }
};

// ==================== PROJECT COSTS ====================
export const getProjectCosts = async (projectId) => {
  try {
    const response = await axios.get(`${API}/project-costs/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching project costs:', error);
    throw error;
  }
};

export const saveProjectCosts = async (costs) => {
  try {
    const response = await axios.post(`${API}/project-costs`, costs);
    return response.data;
  } catch (error) {
    console.error('Error saving project costs:', error);
    throw error;
  }
};

// ==================== SUPPLIERS ====================
export const getSuppliers = async () => {
  try {
    const response = await axios.get(`${API}/suppliers`);
    return response.data;
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    throw error;
  }
};

export const addSupplier = async (supplier) => {
  try {
    const response = await axios.post(`${API}/suppliers`, supplier);
    return response.data;
  } catch (error) {
    console.error('Error adding supplier:', error);
    throw error;
  }
};

export const deleteSupplier = async (id) => {
  try {
    await axios.delete(`${API}/suppliers/${id}`);
  } catch (error) {
    console.error('Error deleting supplier:', error);
    throw error;
  }
};

// ==================== BRANCHES ====================
export const getBranches = async () => {
  try {
    const response = await axios.get(`${API}/branches`);
    return response.data;
  } catch (error) {
    console.error('Error fetching branches:', error);
    throw error;
  }
};

export const addBranch = async (branch) => {
  try {
    const response = await axios.post(`${API}/branches`, branch);
    return response.data;
  } catch (error) {
    console.error('Error adding branch:', error);
    throw error;
  }
};

export const deleteBranch = async (id) => {
  try {
    await axios.delete(`${API}/branches/${id}`);
  } catch (error) {
    console.error('Error deleting branch:', error);
    throw error;
  }
};

// ==================== EXECUTION STEPS ====================
export const getExecutionSteps = async () => {
  try {
    const response = await axios.get(`${API}/execution-steps`);
    return response.data;
  } catch (error) {
    console.error('Error fetching execution steps:', error);
    throw error;
  }
};

export const addExecutionStep = async (step) => {
  try {
    const response = await axios.post(`${API}/execution-steps`, step);
    return response.data;
  } catch (error) {
    console.error('Error adding execution step:', error);
    throw error;
  }
};

export const deleteExecutionStep = async (id) => {
  try {
    await axios.delete(`${API}/execution-steps/${id}`);
  } catch (error) {
    console.error('Error deleting execution step:', error);
    throw error;
  }
};

// ==================== PAYMENT METHODS ====================
export const getPaymentMethods = async () => {
  try {
    const response = await axios.get(`${API}/payment-methods`);
    return response.data;
  } catch (error) {
    console.error('Error fetching payment methods:', error);
    throw error;
  }
};

export const addPaymentMethod = async (method) => {
  try {
    const response = await axios.post(`${API}/payment-methods`, method);
    return response.data;
  } catch (error) {
    console.error('Error adding payment method:', error);
    throw error;
  }
};

export const deletePaymentMethod = async (id) => {
  try {
    await axios.delete(`${API}/payment-methods/${id}`);
  } catch (error) {
    console.error('Error deleting payment method:', error);
    throw error;
  }
};

// ==================== BANK ACCOUNTS ====================
export const getBankAccounts = async () => {
  try {
    const response = await axios.get(`${API}/bank-accounts`);
    return response.data;
  } catch (error) {
    console.error('Error fetching bank accounts:', error);
    throw error;
  }
};

export const addBankAccount = async (account) => {
  try {
    const response = await axios.post(`${API}/bank-accounts`, account);
    return response.data;
  } catch (error) {
    console.error('Error adding bank account:', error);
    throw error;
  }
};

export const deleteBankAccount = async (id) => {
  try {
    await axios.delete(`${API}/bank-accounts/${id}`);
  } catch (error) {
    console.error('Error deleting bank account:', error);
    throw error;
  }
};

// ==================== MANAGERS ====================
export const getManagers = async () => {
  try {
    const response = await axios.get(`${API}/managers`);
    return response.data;
  } catch (error) {
    console.error('Error fetching managers:', error);
    throw error;
  }
};

export const addManager = async (manager) => {
  try {
    const response = await axios.post(`${API}/managers`, manager);
    return response.data;
  } catch (error) {
    console.error('Error adding manager:', error);
    throw error;
  }
};

export const deleteManager = async (name) => {
  try {
    await axios.delete(`${API}/managers/${name}`);
  } catch (error) {
    console.error('Error deleting manager:', error);
    throw error;
  }
};

export const updateManagerPassword = async (name, newPassword) => {
  try {
    const response = await axios.put(`${API}/managers/${encodeURIComponent(name)}/password`, { password: newPassword });
    return response.data;
  } catch (error) {
    console.error('Error updating manager password:', error);
    throw error;
  }
};

// ==================== INVOICES ====================
export const uploadInvoice = async (itemId, file) => {
  try {
    const formData = new FormData();
    formData.append('itemId', itemId);
    formData.append('file', file);
    
    const response = await axios.post(`${API}/invoices/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading invoice:', error);
    throw error;
  }
};

export const getInvoicesByItem = async (itemId) => {
  try {
    const response = await axios.get(`${API}/invoices/${itemId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
};

export const deleteInvoice = async (invoiceId) => {
  try {
    await axios.delete(`${API}/invoices/${invoiceId}`);
  } catch (error) {
    console.error('Error deleting invoice:', error);
    throw error;
  }
};

export const getInvoiceDownloadUrl = (invoiceId) => {
  return `${API}/invoices/download/${invoiceId}`;
};

// ==================== PURCHASE INVOICES ====================
export const checkDuplicateInvoice = async (invoiceNumber) => {
  try {
    const response = await axios.get(`${API}/purchase-invoices/check-duplicate/${encodeURIComponent(invoiceNumber)}`);
    return response.data;
  } catch (error) {
    console.error('Error checking duplicate invoice:', error);
    throw error;
  }
};

export const scanPurchaseInvoice = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API}/purchase-invoices/scan`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error scanning purchase invoice:', error);
    throw error;
  }
};

export const uploadPurchaseInvoice = async (invoiceNumber, invoiceDate, file, totalAmount = '', vatAmount = '') => {
  try {
    const formData = new FormData();
    formData.append('invoiceNumber', invoiceNumber);
    formData.append('invoiceDate', invoiceDate);
    formData.append('totalAmount', totalAmount);
    formData.append('vatAmount', vatAmount);
    formData.append('file', file);
    
    const response = await axios.post(`${API}/purchase-invoices`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading purchase invoice:', error);
    throw error;
  }
};

export const getPurchaseInvoices = async () => {
  try {
    const response = await axios.get(`${API}/purchase-invoices`);
    return response.data;
  } catch (error) {
    console.error('Error fetching purchase invoices:', error);
    throw error;
  }
};

export const linkPurchaseInvoiceToItem = async (invoiceId, linkedItemId, linkedItemType) => {
  try {
    const formData = new FormData();
    formData.append('linkedItemId', linkedItemId);
    formData.append('linkedItemType', linkedItemType);
    
    const response = await axios.put(
      `${API}/purchase-invoices/${invoiceId}/link-to-item`,
      formData
    );
    return response.data;
  } catch (error) {
    console.error('Error linking invoice to item:', error);
    throw error;
  }
};

export const deletePurchaseInvoice = async (invoiceId) => {
  try {
    await axios.delete(`${API}/purchase-invoices/${invoiceId}`);
  } catch (error) {
    console.error('Error deleting purchase invoice:', error);
    throw error;
  }
};

export const getPurchaseInvoiceDownloadUrl = (invoiceId) => {
  return `${API}/purchase-invoices/download/${invoiceId}`;
};

// ==================== INVENTORY ====================
export const createInventoryItem = async (item) => {
  try {
    const response = await axios.post(`${API}/inventory`, item);
    return response.data;
  } catch (error) {
    console.error('Error creating inventory item:', error);
    throw error;
  }
};

export const getInventory = async () => {
  try {
    const response = await axios.get(`${API}/inventory`);
    return response.data;
  } catch (error) {
    console.error('Error fetching inventory:', error);
    throw error;
  }
};

export const updateInventoryItem = async (id, item) => {
  try {
    const response = await axios.put(`${API}/inventory/${id}`, item);
    return response.data;
  } catch (error) {
    console.error('Error updating inventory item:', error);
    throw error;
  }
};

export const deleteInventoryItem = async (id) => {
  try {
    await axios.delete(`${API}/inventory/${id}`);
  } catch (error) {
    console.error('Error deleting inventory item:', error);
    throw error;
  }
};

export const reduceInventoryByName = async (partName, quantity) => {
  try {
    const formData = new FormData();
    formData.append('partName', partName);
    formData.append('quantity', quantity);
    
    const response = await axios.post(`${API}/inventory/reduce-by-name`, formData);
    return response.data;
  } catch (error) {
    console.error('Error reducing inventory:', error);
    throw error;
  }
};

// ==================== DAILY JOURNAL ====================
export const createDailyTask = async (task) => {
  try {
    const response = await axios.post(`${API}/daily-tasks`, task);
    return response.data;
  } catch (error) {
    console.error('Error creating daily task:', error);
    throw error;
  }
};

export const getDailyTasks = async (date = null) => {
  try {
    const url = date ? `${API}/daily-tasks?date=${date}` : `${API}/daily-tasks`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching daily tasks:', error);
    throw error;
  }
};

export const updateDailyTask = async (id, task) => {
  try {
    const response = await axios.put(`${API}/daily-tasks/${id}`, task);
    return response.data;
  } catch (error) {
    console.error('Error updating daily task:', error);
    throw error;
  }
};

export const toggleTaskCompletion = async (id) => {
  try {
    const response = await axios.put(`${API}/daily-tasks/${id}/toggle`);
    return response.data;
  } catch (error) {
    console.error('Error toggling task:', error);
    throw error;
  }
};

export const deleteDailyTask = async (id) => {
  try {
    await axios.delete(`${API}/daily-tasks/${id}`);
  } catch (error) {
    console.error('Error deleting daily task:', error);
    throw error;
  }
};


// ==================== MICROSOFT CALENDAR SYNC ====================
export const syncDailyTaskToCalendar = async (taskId) => {
  try {
    const response = await axios.post(`${API}/daily-tasks/${taskId}/sync-to-calendar`);
    return response.data;
  } catch (error) {
    console.error('Error syncing task to calendar:', error);
    throw error;
  }
};

export const removeDailyTaskFromCalendar = async (taskId) => {
  try {
    const response = await axios.delete(`${API}/daily-tasks/${taskId}/sync-to-calendar`);
    return response.data;
  } catch (error) {
    console.error('Error removing task from calendar:', error);
    throw error;
  }
};

export const syncAllDailyTasksToCalendar = async () => {
  try {
    const response = await axios.post(`${API}/daily-tasks/sync-all-to-calendar`);
    return response.data;
  } catch (error) {
    console.error('Error syncing all tasks to calendar:', error);
    throw error;
  }
};

export const syncDailyTasksFromCalendar = async () => {
  try {
    const response = await axios.post(`${API}/daily-tasks/sync-from-calendar`);
    return response.data;
  } catch (error) {
    console.error('Error syncing tasks from calendar:', error);
    throw error;
  }
};


// ==================== GOOGLE CALENDAR ====================
export const getGoogleCalendarAuthUrl = async () => {
  try {
    const response = await axios.get(`${API}/google-calendar/auth-url`, { params: { origin: window.location.origin } });
    return response.data;
  } catch (error) {
    console.error('Error getting Google Calendar auth URL:', error);
    throw error;
  }
};

export const getGoogleCalendarStatus = async () => {
  try {
    const response = await axios.get(`${API}/google-calendar/status`);
    return response.data;
  } catch (error) {
    console.error('Error getting Google Calendar status:', error);
    return { connected: false };
  }
};

export const disconnectGoogleCalendar = async () => {
  try {
    const response = await axios.delete(`${API}/google-calendar/disconnect`);
    return response.data;
  } catch (error) {
    console.error('Error disconnecting Google Calendar:', error);
    throw error;
  }
};

export const syncTasksToGoogleCalendar = async (date) => {
  try {
    const response = await axios.post(`${API}/google-calendar/sync-tasks`, { date });
    return response.data;
  } catch (error) {
    console.error('Error syncing tasks to Google Calendar:', error);
    throw error;
  }
};

export const pullEventsFromGoogleCalendar = async (days = 30) => {
  try {
    const response = await axios.post(`${API}/google-calendar/pull-events`, { days });
    return response.data;
  } catch (error) {
    console.error('Error pulling events from Google Calendar:', error);
    throw error;
  }
};


// ==================== WORK ====================
export const getWorks = async () => {
  try {
    const timestamp = new Date().getTime();
    const response = await axios.get(`${API}/work?_t=${timestamp}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching works:', error);
    throw error;
  }
};

export const getWork = async (id) => {
  try {
    const response = await axios.get(`${API}/work/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching work:', error);
    throw error;
  }
};

export const addWork = async (work) => {
  try {
    const response = await axios.post(`${API}/work`, work);
    return response.data;
  } catch (error) {
    console.error('Error adding work:', error);
    throw error;
  }
};

export const deleteWork = async (id) => {
  try {
    const response = await axios.delete(`${API}/work/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting work:', error);
    throw error;
  }
};

// ==================== PRICE LIST ====================
export const getPriceItems = async (workId) => {
  try {
    const timestamp = new Date().getTime();
    const response = await axios.get(`${API}/price-items/${workId}?_t=${timestamp}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching price items:', error);
    throw error;
  }
};

export const addPriceItem = async (workId, item) => {
  try {
    const response = await axios.post(`${API}/price-items`, { ...item, workId });
    return response.data;
  } catch (error) {
    console.error('Error adding price item:', error);
    throw error;
  }
};

export const updatePriceItem = async (id, item) => {
  try {
    const response = await axios.put(`${API}/price-items/${id}`, item);
    return response.data;
  } catch (error) {
    console.error('Error updating price item:', error);
    throw error;
  }
};

export const deletePriceItem = async (id) => {
  try {
    const response = await axios.delete(`${API}/price-items/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting price item:', error);
    throw error;
  }
};

export const mergeDuplicatePriceItems = async (workId) => {
  try {
    const response = await axios.post(`${API}/price-items/${workId}/merge-duplicates`);
    return response.data;
  } catch (error) {
    console.error('Error merging duplicate price items:', error);
    throw error;
  }
};

export const bulkUpdatePriceItems = async (workId, percent, ids = null) => {
  try {
    const response = await axios.post(`${API}/price-items/${workId}/bulk-update-prices`, { percent, ids });
    return response.data;
  } catch (error) {
    console.error('Error bulk updating price items:', error);
    throw error;
  }
};


// ==================== WORK DETAILS ====================
export const getWorkDetails = async (workId) => {
  try {
    const timestamp = new Date().getTime();
    const response = await axios.get(`${API}/work-details/${workId}?_t=${timestamp}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching work details:', error);
    throw error;
  }
};

export const addWorkDetail = async (workId, item) => {
  try {
    const response = await axios.post(`${API}/work-details`, { ...item, workId });
    return response.data;
  } catch (error) {
    console.error('Error adding work detail:', error);
    throw error;
  }
};

export const updateWorkDetail = async (id, item) => {
  try {
    const response = await axios.put(`${API}/work-details/${id}`, item);
    return response.data;
  } catch (error) {
    console.error('Error updating work detail:', error);
    throw error;
  }
};

export const deleteWorkDetail = async (id) => {
  try {
    const response = await axios.delete(`${API}/work-details/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting work detail:', error);
    throw error;
  }
};

// ==================== PRODUCT KEY ====================
export const getProductKeys = async () => {
  try {
    const response = await axios.get(`${API}/product-keys`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product keys:', error);
    throw error;
  }
};

export const addProductKey = async (item) => {
  try {
    const response = await axios.post(`${API}/product-keys`, item);
    return response.data;
  } catch (error) {
    console.error('Error adding product key:', error);
    throw error;
  }
};

export const updateProductKey = async (id, item) => {
  try {
    const response = await axios.put(`${API}/product-keys/${id}`, item);
    return response.data;
  } catch (error) {
    console.error('Error updating product key:', error);
    throw error;
  }
};

export const deleteProductKey = async (id) => {
  try {
    await axios.delete(`${API}/product-keys/${id}`);
  } catch (error) {
    console.error('Error deleting product key:', error);
    throw error;
  }
};

// ==================== PROFIT BY CUSTOMER ====================
export const getProfitByCustomer = async () => {
  try {
    const response = await axios.get(`${API}/reports/profit-by-customer`);
    return response.data;
  } catch (error) {
    console.error('Error fetching profit report:', error);
    throw error;
  }
};

// ==================== ALLOWED EMAILS ====================
export const getAllowedEmails = async () => {
  try {
    const response = await axios.get(`${API}/allowed-emails`);
    return response.data;
  } catch (error) {
    console.error('Error fetching allowed emails:', error);
    throw error;
  }
};

export const addAllowedEmail = async (email) => {
  try {
    const response = await axios.post(`${API}/allowed-emails`, { email });
    return response.data;
  } catch (error) {
    console.error('Error adding allowed email:', error);
    throw error;
  }
};

export const deleteAllowedEmail = async (id) => {
  try {
    await axios.delete(`${API}/allowed-emails/${id}`);
  } catch (error) {
    console.error('Error deleting allowed email:', error);
    throw error;
  }
};

// ==================== CHECKS REPORT ====================
export const getChecksReport = async () => {
  try {
    const response = await axios.get(`${API}/reports/checks`);
    return response.data;
  } catch (error) {
    console.error('Error fetching checks report:', error);
    throw error;
  }
};

export const toggleCheckDeposited = async (data) => {
  try {
    const response = await axios.put(`${API}/reports/checks/toggle-deposited`, data);
    return response.data;
  } catch (error) {
    console.error('Error toggling check deposited:', error);
    throw error;
  }
};

export const exportChecksExcel = () => {
  window.open(`${API}/reports/checks/export/excel`, '_blank');
};

export const toggleCheckInvoice = async (data) => {
  try {
    const response = await axios.put(`${API}/reports/checks/toggle-invoice`, data);
    return response.data;
  } catch (error) {
    console.error('Error toggling check invoice:', error);
    throw error;
  }
};

// ==================== UNIFIED CUSTOMERS ====================
export const getCustomers = async () => {
  try {
    const response = await axios.get(`${API}/customers`);
    return response.data;
  } catch (error) {
    console.error('Error fetching customers:', error);
    throw error;
  }
};

export const addCustomer = async (customer) => {
  try {
    const response = await axios.post(`${API}/customers`, customer);
    return response.data;
  } catch (error) {
    console.error('Error adding customer:', error);
    throw error;
  }
};

export const updateCustomer = async (id, customer) => {
  try {
    const response = await axios.put(`${API}/customers/${id}`, customer);
    return response.data;
  } catch (error) {
    console.error('Error updating customer:', error);
    throw error;
  }
};

export const getLockouts = async () => {
  const response = await axios.get(`${API}/auth/lockouts`, { withCredentials: true, headers: authHeader() });
  return response.data;
};

export const clearLockout = async (payload) => {
  const response = await axios.post(`${API}/auth/lockouts/clear`, payload, { withCredentials: true, headers: authHeader() });
  return response.data;
};

export const requestUnlockEmail = async (name) => {
  const response = await axios.post(`${API}/auth/unlock-request`, { name, origin: window.location.origin });
  return response.data;
};

export const getReportPrefs = async () => {
  const response = await axios.get(`${API}/report-prefs`, { withCredentials: true, headers: authHeader() });
  return response.data;
};

export const toggleFavoriteReport = async (path) => {
  const response = await axios.post(`${API}/report-prefs/favorites/toggle`, { path }, { withCredentials: true, headers: authHeader() });
  return response.data;
};

export const addRecentReport = async (path) => {
  const response = await axios.post(`${API}/report-prefs/recent`, { path }, { withCredentials: true, headers: authHeader() });
  return response.data;
};

export const getCustomerCard = async (id) => {
  const response = await axios.get(`${API}/customers/${id}/card`);
  return response.data;
};

export const updateCustomerNumber = async (id, customerNumber) => {
  const response = await axios.put(`${API}/customers/${id}/number`, { customerNumber }, { withCredentials: true, headers: authHeader() });
  return response.data;
};

export const deleteCustomer = async (id) => {
  try {
    await axios.delete(`${API}/customers/${id}`);
  } catch (error) {
    console.error('Error deleting customer:', error);
    throw error;
  }
};

export const importCustomersFromLogs = async () => {
  try {
    const response = await axios.post(`${API}/customers/import-from-logs`);
    return response.data;
  } catch (error) {
    console.error('Error importing customers:', error);
    throw error;
  }
};

// ==================== NOTIFICATION SETTINGS ====================
export const getNotificationSettings = async () => {
  try {
    const response = await axios.get(`${API}/notification-settings`);
    return response.data;
  } catch (error) {
    console.error('Error fetching notification settings:', error);
    throw error;
  }
};

export const updateNotificationSettings = async (settings) => {
  try {
    const response = await axios.put(`${API}/notification-settings`, settings);
    return response.data;
  } catch (error) {
    console.error('Error updating notification settings:', error);
    throw error;
  }
};

export const sendTestEmail = async (recipientEmail) => {
  try {
    const response = await axios.post(`${API}/notification-settings/test-email`, { recipientEmail });
    return response.data;
  } catch (error) {
    console.error('Error sending test email:', error);
    throw error;
  }
};

export const sendDebtAlert = async () => {
  try {
    const response = await axios.post(`${API}/notification-settings/send-debt-alert`);
    return response.data;
  } catch (error) {
    console.error('Error sending debt alert:', error);
    throw error;
  }
};



export const downloadBackup = () => {
  // Open download in new window
  window.open(`${API}/backup/download`, '_blank');
};

export const getBackupDownloadUrl = () => {
  return `${API}/backup/download`;
};

export const restoreBackup = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API}/backup/restore`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error restoring backup:', error);
    throw error;
  }
};

// ==================== GOOGLE DRIVE BACKUP ====================
export const connectGoogleDrive = async () => {
  try {
    const response = await axios.get(`${API}/drive/connect`);
    return response.data;
  } catch (error) {
    console.error('Error connecting to Drive:', error);
    throw error;
  }
};

export const getDriveStatus = async () => {
  try {
    const response = await axios.get(`${API}/drive/status`);
    return response.data;
  } catch (error) {
    console.error('Error getting Drive status:', error);
    throw error;
  }
};

export const backupToDrive = async () => {
  try {
    const response = await axios.post(`${API}/backup/drive`);
    return response.data;
  } catch (error) {
    console.error('Error backing up to Drive:', error);
    throw error;
  }
};

// ==================== SCHEDULED BACKUP ====================
export const getBackupSchedule = async () => {
  try {
    const response = await axios.get(`${API}/backup/schedule`);
    return response.data;
  } catch (error) {
    console.error('Error getting backup schedule:', error);
    throw error;
  }
};

export const setBackupSchedule = async (day, hour, enabled = true) => {
  try {
    const formData = new FormData();
    formData.append('day', day);
    formData.append('hour', hour);
    formData.append('enabled', enabled);
    
    const response = await axios.post(`${API}/backup/schedule`, formData);
    return response.data;
  } catch (error) {
    console.error('Error setting backup schedule:', error);
    throw error;
  }
};

// ==================== RECYCLE BIN ====================
export const getRecycleBin = async (itemType) => {
  try {
    const params = itemType ? `?item_type=${itemType}` : '';
    const response = await axios.get(`${API}/recycle-bin${params}`);
    return response.data;
  } catch (error) {
    console.error('Error getting recycle bin:', error);
    throw error;
  }
};

export const restoreFromRecycleBin = async (itemId) => {
  try {
    const response = await axios.post(`${API}/recycle-bin/${itemId}/restore`);
    return response.data;
  } catch (error) {
    console.error('Error restoring item:', error);
    throw error;
  }
};

export const permanentlyDeleteFromRecycleBin = async (itemId) => {
  try {
    const response = await axios.delete(`${API}/recycle-bin/${itemId}`);
    return response.data;
  } catch (error) {
    console.error('Error permanently deleting item:', error);
    throw error;
  }
};

export const emptyRecycleBin = async () => {
  try {
    const response = await axios.delete(`${API}/recycle-bin`);
    return response.data;
  } catch (error) {
    console.error('Error emptying recycle bin:', error);
    throw error;
  }
};

// ==================== SESSIONS ====================
export const createLoginSession = async (username) => {
  try {
    const response = await axios.post(`${API}/sessions`, { username });
    return response.data;
  } catch (error) {
    console.error('Error creating session:', error);
    throw error;
  }
};

export const getLoginSessions = async () => {
  try {
    const response = await axios.get(`${API}/sessions`);
    return response.data;
  } catch (error) {
    console.error('Error getting sessions:', error);
    throw error;
  }
};

export const disconnectSession = async (sessionId) => {
  try {
    const response = await axios.delete(`${API}/sessions/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error('Error disconnecting session:', error);
    throw error;
  }
};

export const disconnectAllSessions = async (currentSessionId) => {
  try {
    const params = currentSessionId ? `?current_session_id=${currentSessionId}` : '';
    const response = await axios.delete(`${API}/sessions${params}`);
    return response.data;
  } catch (error) {
    console.error('Error disconnecting all sessions:', error);
    throw error;
  }
};

export const sessionHeartbeat = async (sessionId) => {
  try {
    await axios.put(`${API}/sessions/${sessionId}/heartbeat`);
  } catch (error) {
    // Silent fail for heartbeat
  }
};

// ==================== SUSPICIOUS LOGINS ====================
export const getSuspiciousLogins = async () => {
  try {
    const response = await axios.get(`${API}/suspicious-logins`);
    return response.data;
  } catch (error) {
    console.error('Error getting suspicious logins:', error);
    throw error;
  }
};

export const dismissSuspiciousLogin = async (itemId) => {
  try {
    const response = await axios.put(`${API}/suspicious-logins/${itemId}/dismiss`);
    return response.data;
  } catch (error) {
    console.error('Error dismissing suspicious login:', error);
    throw error;
  }
};

export const clearSuspiciousLogins = async () => {
  try {
    const response = await axios.delete(`${API}/suspicious-logins`);
    return response.data;
  } catch (error) {
    console.error('Error clearing suspicious logins:', error);
    throw error;
  }
};

// ==================== SECURITY ALERT SETTINGS ====================
export const getSecurityAlertSettings = async () => {
  try {
    const response = await axios.get(`${API}/security-alert-settings`);
    return response.data;
  } catch (error) {
    console.error('Error getting security alert settings:', error);
    throw error;
  }
};

export const updateSecurityAlertSettings = async (settings) => {
  try {
    const response = await axios.put(`${API}/security-alert-settings`, settings);
    return response.data;
  } catch (error) {
    console.error('Error updating security alert settings:', error);
    throw error;
  }
};

// ==================== CONSOLIDATED INVOICES ====================
export const createConsolidatedInvoice = async (data) => {
  try {
    const response = await axios.post(`${API}/consolidated-invoices`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating consolidated invoice:', error);
    throw error;
  }
};

export const getConsolidatedInvoices = async () => {
  try {
    const response = await axios.get(`${API}/consolidated-invoices`);
    return response.data;
  } catch (error) {
    console.error('Error getting consolidated invoices:', error);
    throw error;
  }
};

export const getConsolidatedInvoice = async (id) => {
  try {
    const response = await axios.get(`${API}/consolidated-invoices/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error getting consolidated invoice:', error);
    throw error;
  }
};

export const updateConsolidatedInvoice = async (id, data) => {
  try {
    const response = await axios.put(`${API}/consolidated-invoices/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating consolidated invoice:', error);
    throw error;
  }
};

export const deleteConsolidatedInvoice = async (id) => {
  try {
    const response = await axios.delete(`${API}/consolidated-invoices/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting consolidated invoice:', error);
    throw error;
  }
};

export const getCustomerBalance = async (customerName) => {
  try {
    const response = await axios.get(`${API}/customer-balance/${encodeURIComponent(customerName)}`);
    return response.data;
  } catch (error) {
    console.error('Error getting customer balance:', error);
    return { totalBalance: 0, invoiceCount: 0, hasDebt: false, hasSurplus: false };
  }
};


// ==================== MONTHLY PROFIT ====================
export const getMonthlyProfit = async () => {
  try {
    const response = await axios.get(`${API}/reports/monthly-profit`);
    return response.data;
  } catch (error) {
    console.error('Error fetching monthly profit:', error);
    throw error;
  }
};


// ==================== GLOBAL SEARCH ====================
export const globalSearch = async (query) => {
  try {
    const response = await axios.get(`${API}/search`, { params: { q: query } });
    return response.data;
  } catch (error) {
    console.error('Error in global search:', error);
    throw error;
  }
};


// ==================== CUSTOMERS EXPORT ====================
export const exportCustomersExcel = () => {
  return `${API}/customers/export/excel`;
};


// ==================== AUTH LOGIN ====================
export const loginManager = async (name, password) => {
  try {
    const response = await axios.post(`${API}/auth/login`, { name, password }, { withCredentials: true });
    return response.data;
  } catch (error) {
    throw error;
  }
};



// ==================== NOTES ====================
export const getNotes = async () => {
  try {
    const response = await axios.get(`${API}/notes`);
    return response.data;
  } catch (error) {
    console.error('Error fetching notes:', error);
    throw error;
  }
};

export const getCompletedNotes = async () => {
  try {
    const response = await axios.get(`${API}/notes/completed`);
    return response.data;
  } catch (error) {
    console.error('Error fetching completed notes:', error);
    throw error;
  }
};

export const createNote = async (text, color, pinned = false, reminder = null) => {
  try {
    const response = await axios.post(`${API}/notes`, { text, color, pinned, reminder });
    return response.data;
  } catch (error) {
    console.error('Error creating note:', error);
    throw error;
  }
};

export const updateNote = async (id, data) => {
  try {
    const response = await axios.put(`${API}/notes/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating note:', error);
    throw error;
  }
};

export const deleteNote = async (id) => {
  try {
    await axios.delete(`${API}/notes/${id}`);
  } catch (error) {
    console.error('Error deleting note:', error);
    throw error;
  }
};


export const reorderNotes = async (order) => {
  try {
    const response = await axios.put(`${API}/notes/reorder`, { order });
    return response.data;
  } catch (error) {
    console.error('Error reordering notes:', error);
    throw error;
  }
};


// ==================== SUPPORT TICKETS ====================
export const getSupportTickets = async () => {
  try {
    const response = await axios.get(`${API}/support-tickets`);
    return response.data;
  } catch (error) {
    console.error('Error fetching support tickets:', error);
    throw error;
  }
};

export const createSupportTicket = async (ticket) => {
  try {
    const response = await axios.post(`${API}/support-tickets`, ticket);
    return response.data;
  } catch (error) {
    console.error('Error creating support ticket:', error);
    throw error;
  }
};

export const updateSupportTicket = async (id, data) => {
  try {
    const response = await axios.put(`${API}/support-tickets/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating support ticket:', error);
    throw error;
  }
};

export const deleteSupportTicket = async (id) => {
  try {
    await axios.delete(`${API}/support-tickets/${id}`);
  } catch (error) {
    console.error('Error deleting support ticket:', error);
    throw error;
  }
};
